import {
	isFixRelayIdConflictForHelpObjectStoreEnabled,
	isFixRelayIdConflictInHelpCenter,
} from '@helpcenter/feature-flags';
import { Environment, Network, RecordSource, Store } from 'relay-runtime';
import type { Variables, RequestParameters, CacheConfig, UploadableMap } from 'relay-runtime';
import type { GetDataID } from 'relay-runtime/lib/store/RelayResponseNormalizer';
import { getIsPageServerSideRendered } from '@atlassian/help-center-common-util/env';
import RelayDataID from '@atlassian/relay-data-id';
import fetchGraphQL from '../fetch';
import fetchGraphQLWithObservable from '../fetch-v2';
import { subscribe } from '../subscribe';
import { isSSR } from '../subscription-constants/subscription-constants';

const fetchRelay = (
	params: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
	uploadables: UploadableMap,
) => {
	const fetcher =
		__SERVER__ || getIsPageServerSideRendered() ? fetchGraphQLWithObservable : fetchGraphQL;

	return fetcher(params, variables, cacheConfig, uploadables);
};

const getNetWork = () => {
	if (isSSR()) return Network.create(fetchRelay);
	return Network.create(fetchRelay, subscribe);
};

/**
 * GraphQL type names whose `id` values are known to collide across types in the
 * Help Center Relay store (e.g. the same backend id is reused by multiple
 * Help Object Store entities). For these types, we namespace the Relay record
 * id with the type name to avoid cache poisoning between unrelated records.
 */
export const HELP_OBJECT_STORE_TYPES_WITH_ID_CONFLICTS: ReadonlySet<string> = new Set([
	'HelpObjectStoreEntityData',
	'JiraServiceManagementRequestType',
	'HelpObjectStorePortalGroup',
	'HelpObjectStoreKbCategories',
	'HelpObjectStoreRequestForm',
	'HelpObjectStoreHelpObject',
	'HelpObjectStorePortalGroupRequestForm',
]);

const getHelpObjectStoreDataID: GetDataID = (fieldValue, typeName) => {
	if (HELP_OBJECT_STORE_TYPES_WITH_ID_CONFLICTS.has(typeName)) {
		return RelayDataID(fieldValue, typeName);
	}

	// Mirror Relay's default id derivation for all other types. Copied the implementation from RelayDataID.
	const { id } = fieldValue;
	return id == null ? undefined : String(id);
};

const resolveGetDataID = (): GetDataID | undefined => {
	if (isFixRelayIdConflictForHelpObjectStoreEnabled()) {
		return getHelpObjectStoreDataID;
	}
	if (isFixRelayIdConflictInHelpCenter()) {
		return RelayDataID;
	}
	return undefined;
};

const getDataID = resolveGetDataID();

// Export a singleton instance of Relay Environment configured with our network function:
export default new Environment({
	network: getNetWork(),
	store: new Store(new RecordSource()),
	...(getDataID ? { getDataID } : {}),
});
